import {
  Badge,
  Card,
  Code,
  CopyButton,
  Button,
  Group,
  List,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import type { AppWebhook, OrgApp } from "./types";
import { formatDate } from "./types";

export function CredentialsTab({
  orgApp,
  webhooks,
}: {
  orgApp: OrgApp;
  webhooks: AppWebhook[];
}) {
  const { configuration } = orgApp;

  return (
    <Stack gap="lg">
      {/* API Credentials */}
      <Title order={5}>API Credentials</Title>

      <Card withBorder>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Client ID
          </Text>
          <Group gap="xs">
            <Code style={{ flex: 1 }}>{orgApp.id}</Code>
            <CopyButton value={orgApp.id}>
              {({ copy, copied }) => (
                <Button size="xs" variant="subtle" onClick={copy}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Client Type
          </Text>
          <Badge
            color={orgApp.public ? "blue" : "orange"}
            variant="light"
            size="lg"
          >
            {orgApp.public ? "Public" : "Confidential"}
          </Badge>
          <Text size="xs" c="dimmed">
            {orgApp.public
              ? "Public clients do not use a client secret (e.g. SPAs, mobile apps)."
              : "Confidential clients use a client secret for server-to-server auth."}
          </Text>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            OAuth2 Scopes
          </Text>
          {configuration.scope ? (
            <Group gap={6}>
              {configuration.scope
                .split(" ")
                .filter(Boolean)
                .map((s) => (
                  <Badge key={s} variant="outline" size="sm">
                    {s}
                  </Badge>
                ))}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              No scopes configured
            </Text>
          )}
        </Stack>
      </Card>

      {/* Redirect URLs */}
      <Title order={5}>Redirect URLs</Title>

      <Card withBorder>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Redirect URLs
          </Text>
          {configuration.redirectUrls.length > 0 ? (
            <List size="sm" spacing={4}>
              {configuration.redirectUrls.map((url) => (
                <List.Item key={url}>
                  <Code>{url}</Code>
                </List.Item>
              ))}
            </List>
          ) : (
            <Text size="sm" c="dimmed">
              No redirect URLs configured
            </Text>
          )}
        </Stack>
      </Card>

      {configuration.logoutRedirectUrls.length > 0 && (
        <Card withBorder>
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Logout Redirect URLs
            </Text>
            <List size="sm" spacing={4}>
              {configuration.logoutRedirectUrls.map((url) => (
                <List.Item key={url}>
                  <Code>{url}</Code>
                </List.Item>
              ))}
            </List>
          </Stack>
        </Card>
      )}

      {/* Auth Flow */}
      <Title order={5}>Auth Flow</Title>

      <Card withBorder>
        <Group gap="xl">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Login URL
            </Text>
            <Code>{configuration.metadata.loginUrl || "\u2014"}</Code>
          </Stack>
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Consent URL
            </Text>
            <Code>{configuration.metadata.consentUrl || "\u2014"}</Code>
          </Stack>
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Requires Consent
            </Text>
            <Badge
              variant="outline"
              color={
                configuration.metadata.requiresConsent ? "yellow" : "green"
              }
              size="sm"
            >
              {configuration.metadata.requiresConsent ? "Yes" : "No"}
            </Badge>
          </Stack>
        </Group>
      </Card>

      {/* Webhooks */}
      <Title order={5}>Webhooks ({webhooks.length})</Title>
      <Text size="sm" c="dimmed">
        Webhooks receive real-time event notifications when actions occur in
        organizations that have your app installed.
      </Text>

      {webhooks.length === 0 && (
        <Card withBorder padding="xl">
          <Text c="dimmed" ta="center" py="md">
            No webhooks configured. Webhooks are created when your app is
            installed by an organization and your manifest declares
            webhookEvents.
          </Text>
        </Card>
      )}

      {webhooks.map((wh) => (
        <Card key={wh.id} withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Group gap="xs">
                <Badge
                  color={wh.active ? "green" : "red"}
                  variant="light"
                  size="sm"
                >
                  {wh.active ? "Active" : "Inactive"}
                </Badge>
                <Code>{wh.url}</Code>
              </Group>
              <Text size="xs" c="dimmed">
                Created {formatDate(wh.createdAt)}
              </Text>
            </Group>

            <Stack gap={4}>
              <Text size="xs" fw={500}>
                Subscribed Events ({wh.events.length})
              </Text>
              <Group gap={6}>
                {wh.events.map((evt) => (
                  <Badge key={evt} variant="outline" size="xs" color="violet">
                    {evt}
                  </Badge>
                ))}
              </Group>
            </Stack>

            {wh.installationId && (
              <Group gap={4}>
                <Text size="xs" c="dimmed">
                  Installation:
                </Text>
                <Code style={{ fontSize: 11 }}>{wh.installationId}</Code>
              </Group>
            )}
          </Stack>
        </Card>
      ))}

      {/* Creator Info */}
      <Card withBorder>
        <Group gap="md">
          <Text size="xs" c="dimmed">
            Created by {orgApp.creator.email} on {formatDate(orgApp.createdAt)}
          </Text>
        </Group>
      </Card>
    </Stack>
  );
}
